/**
 * Spec №22 — the task-bound execution thread (D-CL1). Read + the run verbs.
 *
 * The thread IS the execution record: `useTaskThread` reads the bound conversation
 * plus every run's stamps (venue · executor · requested_by · tokens). `usePlayTaskRun`
 * is ▶ — it runs the agent on the task under the agent's own scope (venue = native),
 * bound to this thread; the human interjects by playing again with a message.
 * `useTakeOverTask` is the explicit escape (the agent yields). Every mutation
 * invalidates the thread + the Needs-You inbox so the loop stays live.
 */

import { useIsMutating, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { mcpClient } from "../../lib/mcp-client";
import type { TaskThreadResult, TaskLaunchLinks } from "../../lib/types";

const STALE_MS = 5_000;

export function useTaskThread(taskId: string | undefined, enabled = true) {
  return useQuery<TaskThreadResult>({
    queryKey: ["task-thread", taskId],
    queryFn: () => mcpClient.getTaskThread(taskId as string),
    enabled: enabled && !!taskId,
    staleTime: STALE_MS,
  });
}

/** D-CL5 — the Claude / ChatGPT deep links (fetched lazily when the picker opens). */
export function useTaskLaunchLinks(taskId: string | undefined, enabled: boolean) {
  return useQuery<TaskLaunchLinks>({
    queryKey: ["task-launch-links", taskId],
    queryFn: () => mcpClient.getTaskLaunchLinks(taskId as string),
    enabled: enabled && !!taskId,
    staleTime: 60_000,
  });
}

/**
 * №22.5 (F2) — the run's in-flight state is SHARED, keyed by task.
 *
 * A run started on the desk and the thread that shows it are two different component
 * instances, so `play.isPending` was private to whichever surface pressed the button:
 * the desk focused the panel correctly, the panel opened on the task, and then showed
 * an EMPTY thread for the whole run — because nothing there knew a run was in flight
 * and the transcript is only appended when the run finishes.
 *
 * David's rule, verbatim: *no agent action should EVER happen without a chat surface
 * for the user to see.* Focusing the panel was half of it; the other half is that the
 * panel must be able to SEE the run, wherever it was started from. A mutation key does
 * that — `useIsRunPlaying(taskId)` observes it from anywhere.
 */
export const PLAY_RUN_KEY = "play-task-run" as const;

/**
 * №22.6.1 (D-NY6 §4, work item B) — answering or skipping a card can now BLOCK on a
 * new run (`fireResumeIfNeeded` awaits `runAgent` before the tool call returns, once
 * the batch is exhausted — item A). Before this, the only thing that ever started a
 * run was ▶ (`PLAY_RUN_KEY`), so `useIsRunPlaying` never needed to look further; now
 * an option click or skip can take exactly as long as a run does, and — R5d — the
 * SAME derived signal is what §"answeredAndIdle" below reads to decide whether to
 * show "paused, press Continue". Missing this would let the two disagree on one
 * screen: the resume genuinely in flight, and the thread simultaneously offering a
 * stale "Continue" button for a run that has already started.
 */
export const ANSWER_NEEDS_YOU_KEY = "answer-needs-you" as const;
export const SKIP_NEEDS_YOU_KEY = "skip-needs-you" as const;
const RUN_IN_FLIGHT_KEYS: readonly string[] = [PLAY_RUN_KEY, ANSWER_NEEDS_YOU_KEY, SKIP_NEEDS_YOU_KEY];

// ---------------------------------------------------------------------------
// №22.5 (F8) — OPTIMISTIC THREAD WRITES. The pattern, once, instead of patches.
//
// David: *"The moment an action is submitted to an agent, it needs to be in the chat.
// It's a pre-requisite actually."* He is right, and the cause was structural: every
// thread mutation was write-then-refetch. A run blocks for its whole duration and the
// transcript is only appended at the end, so pressing ▶ — or answering a card — put
// the request on the wire and left the conversation showing nothing at all. The
// evidence that anything had happened arrived with the result.
//
// The fix is react-query's own optimistic-update contract, applied uniformly:
//
//   onMutate   → cancel in-flight reads of this thread (so a slow refetch cannot land
//                on top of our write), snapshot the cache, append the turn locally.
//   onError    → restore the snapshot. The optimistic turn disappears with the failure
//                that caused it, and the surface's own error line explains it.
//   onSettled  → invalidate. The SERVER's transcript replaces ours wholesale — we never
//                merge the two, so the local copy can never drift into the record.
//
// Optimistic turns are stamped `pending: true` and render dimmed. That is not
// decoration: until the server confirms, the line is a claim about a request in flight,
// not a record of something that happened, and this repo does not let those look alike.
// ---------------------------------------------------------------------------

const THREAD_KEY = (taskId: string) => ["task-thread", taskId] as const;

/** A locally-appended turn, indistinguishable from nothing once the server answers. */
export interface OptimisticTurn {
  role: "user" | "assistant";
  text: string;
  metadata?: Record<string, unknown>;
}

/** Append turns to the cached thread and return the snapshot needed to undo it. */
function appendOptimistic(
  qc: ReturnType<typeof useQueryClient>,
  taskId: string,
  turns: OptimisticTurn[],
): { previous: TaskThreadResult | undefined } {
  const key = THREAD_KEY(taskId);
  const previous = qc.getQueryData<TaskThreadResult>(key);
  const stamp = new Date().toISOString();
  const made = turns.map((t, i) => ({
    id: `optimistic_${stamp}_${i}`,
    role: t.role,
    parts: [{ type: "text" as const, text: t.text }],
    metadata: { ...(t.metadata ?? {}), pending: true, at: stamp },
  }));
  qc.setQueryData<TaskThreadResult>(key, (old) => {
    // No thread cached yet (the panel just opened on it) — there is nothing to append
    // to without inventing a conversation shell, and the refetch is moments away.
    if (!old?.conversation) return old;
    return {
      ...old,
      conversation: { ...old.conversation, messages: [...(old.conversation.messages ?? []), ...made] },
    } as TaskThreadResult;
  });
  return { previous };
}

function rollbackOptimistic(
  qc: ReturnType<typeof useQueryClient>,
  taskId: string,
  ctx: { previous: TaskThreadResult | undefined } | undefined,
): void {
  if (ctx?.previous !== undefined) qc.setQueryData(THREAD_KEY(taskId), ctx.previous);
}

export function usePlayTaskRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: [PLAY_RUN_KEY],
    mutationFn: (p: { agentId: string; taskId: string; input: string; requestedByName?: string }) =>
      mcpClient.playTaskRun(p.agentId, { task_id: p.taskId, input: p.input, requested_by_name: p.requestedByName }),
    // F8 — the human's words land in the chat the instant they press send, not when the
    // run returns. A bare ▶ (no input) appends nothing: the run-start line and the
    // working indicator already say what is happening, and a blank bubble would not.
    onMutate: async (p) => {
      await qc.cancelQueries({ queryKey: THREAD_KEY(p.taskId) });
      const said = p.input.trim();
      return said
        ? appendOptimistic(qc, p.taskId, [{ role: "user", text: said }])
        : { previous: qc.getQueryData<TaskThreadResult>(THREAD_KEY(p.taskId)) };
    },
    onError: (_e, p, ctx) => rollbackOptimistic(qc, p.taskId, ctx),
    onSettled: (_r, _e, p) => {
      qc.invalidateQueries({ queryKey: THREAD_KEY(p.taskId) });
      qc.invalidateQueries({ queryKey: ["task-detail", p.taskId] });
      qc.invalidateQueries({ queryKey: ["needs-you"] });
      // №22.6 (H2/O1) — the answered ledger is the account of THIS act; refresh it
      // with the inbox so the strip lands at the moment of the answer, not a poll later.
      qc.invalidateQueries({ queryKey: ["answered-decisions"] });
      qc.invalidateQueries({ queryKey: ["agent-brain-snapshot"] });
    },
  });
}

/**
 * Is a run in flight for THIS task, started anywhere in the app? Matches on the
 * mutation's own variables, so a run on another task never makes this thread claim to
 * be working — which would be simulated liveness, the thing the repo bans.
 *
 * №22.6.1 (work item B) — "started anywhere" now includes answer/skip, not just ▶:
 * see the comment on `RUN_IN_FLIGHT_KEYS` above.
 */
export function useIsRunPlaying(taskId: string | undefined): boolean {
  return useIsMutating({
    predicate: (m) =>
      !!taskId
      && RUN_IN_FLIGHT_KEYS.includes(String(m.options.mutationKey?.[0]))
      && (m.state.variables as { taskId?: string } | undefined)?.taskId === taskId,
  }) > 0;
}

export function useTakeOverTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { taskId: string }) => mcpClient.takeOverTask(p.taskId),
    // F8 — no optimistic THREAD turn: taking over has no line of its own, its visible
    // effect is derived from `taken_over_by_user_id` on the task. But it reconciles on
    // SETTLED like the rest, so a failed take-over re-reads the truth instead of
    // leaving the surface showing whatever it last believed.
    onSettled: (_r, _e, p) => {
      qc.invalidateQueries({ queryKey: THREAD_KEY(p.taskId) });
      qc.invalidateQueries({ queryKey: ["task-detail", p.taskId] });
      qc.invalidateQueries({ queryKey: ["needs-you"] });
      // №22.6 (H2/O1) — the answered ledger is the account of THIS act; refresh it
      // with the inbox so the strip lands at the moment of the answer, not a poll later.
      qc.invalidateQueries({ queryKey: ["answered-decisions"] });
    },
  });
}

/**
 * №22.1 (D-CC2) — answer a decision card by choosing a prepared option. Executes the
 * snapshot server-side, then invalidates EVERY surface that renders the card so the
 * answer clears everywhere (D-CC4): the thread, the task detail, the Needs-You home,
 * the agent snapshot (desk), and the workforce roll-up (the D-CC9 dot).
 */
export function useAnswerNeedsYou() {
  const qc = useQueryClient();
  return useMutation({
    // №22.6.1 (work item B) — a stable key so `useIsRunPlaying` can find this
    // mutation the same way it already finds `usePlayTaskRun`'s.
    mutationKey: [ANSWER_NEEDS_YOU_KEY],
    // `optionLabel` is carried for the OPTIMISTIC line only — the server executes the
    // option by INDEX against its own snapshot (D-CC3), and never trusts a label the
    // client sends.
    mutationFn: (p: { taskId: string; optionIndex: number; optionLabel?: string }) =>
      mcpClient.answerNeedsYou(p.taskId, p.optionIndex),
    // F8 — choosing an option executes a prepared verb server-side, which can take a
    // moment. The choice appears in the thread immediately; the server's consequence
    // chain (choice → action → consequence) replaces it when it lands.
    onMutate: async (p) => {
      await qc.cancelQueries({ queryKey: THREAD_KEY(p.taskId) });
      const label = p.optionLabel?.trim();
      return appendOptimistic(qc, p.taskId, [{
        role: "assistant",
        text: label ? `✓ you chose: ${label}` : "✓ answering…",
        metadata: { needs_you_answer: true, outcome: "executed" },
      }]);
    },
    onError: (_e, p, ctx) => rollbackOptimistic(qc, p.taskId, ctx),
    onSettled: (_r, _e, p) => {
      qc.invalidateQueries({ queryKey: THREAD_KEY(p.taskId) });
      qc.invalidateQueries({ queryKey: ["task-detail", p.taskId] });
      qc.invalidateQueries({ queryKey: ["needs-you"] });
      // №22.6 (H2/O1) — the answered ledger is the account of THIS act; refresh it
      // with the inbox so the strip lands at the moment of the answer, not a poll later.
      qc.invalidateQueries({ queryKey: ["answered-decisions"] });
      qc.invalidateQueries({ queryKey: ["agent-brain-snapshot"] });
      qc.invalidateQueries({ queryKey: ["workforce-status"] });
    },
  });
}

/**
 * №22.5 (C4) — skip the standing card. Invalidates exactly what answering does, and
 * for the same reason: with a BATCH the card does not disappear, it ADVANCES, so every
 * surface reading `["needs-you"]` has to re-read to show the next question rather than
 * keep rendering the one just skipped (D-CC4 — one card, one cursor, all surfaces).
 */
export function useSkipNeedsYou() {
  const qc = useQueryClient();
  return useMutation({
    // №22.6.1 (work item B) — see useAnswerNeedsYou's mutationKey comment.
    mutationKey: [SKIP_NEEDS_YOU_KEY],
    mutationFn: (p: { taskId: string }) => mcpClient.skipNeedsYou(p.taskId),
    onMutate: async (p) => {   // F8
      await qc.cancelQueries({ queryKey: THREAD_KEY(p.taskId) });
      return appendOptimistic(qc, p.taskId, [{
        role: "assistant",
        text: "⊘ you skipped this",
        metadata: { needs_you_answer: true, outcome: "skipped" },
      }]);
    },
    onError: (_e, p, ctx) => rollbackOptimistic(qc, p.taskId, ctx),
    onSettled: (_r, _e, p) => {
      qc.invalidateQueries({ queryKey: THREAD_KEY(p.taskId) });
      qc.invalidateQueries({ queryKey: ["task-detail", p.taskId] });
      qc.invalidateQueries({ queryKey: ["needs-you"] });
      // №22.6 (H2/O1) — the answered ledger is the account of THIS act; refresh it
      // with the inbox so the strip lands at the moment of the answer, not a poll later.
      qc.invalidateQueries({ queryKey: ["answered-decisions"] });
      qc.invalidateQueries({ queryKey: ["agent-brain-snapshot"] });
      qc.invalidateQueries({ queryKey: ["workforce-status"] });
    },
  });
}
